import { useTranslations } from '@vocab/react';
import {
  Button,
  Dialog,
  Inline,
  MenuItem,
  OverflowMenu,
  Text,
  Box,
  MenuItemLink,
  useToast,
} from 'braid-design-system';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { COMMUNITY_GUIDLINES } from 'src/core/domain/constants';
import errorTranslations from 'src/core/domain/errors/.vocab';
import { DeleteError } from 'src/core/domain/errors/deleteErrors';
import { NAV_PATHS } from 'src/paths';
import { useOptionDialog } from 'src/provider/optionDialog';
import { useThreadDetail } from 'src/provider/threadDetail';
import {
  deleteComment,
  deleteReply,
  deleteThread,
} from 'src/services/threadInteractionService';

import translations from './.vocab';

import * as styles from './DeleteButton.css';

export default () => {
  const showToast = useToast();
  const navigate = useNavigate();
  const { threadDetailDispatch } = useThreadDetail();
  const { t } = useTranslations(translations);
  const { t: errorTranslation } = useTranslations(errorTranslations);

  const {
    optionDialogState: { interaction, id },
  } = useOptionDialog();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleDialogClose = () => setIsDialogOpen(false);
  const handleDialogOpen = () => setIsDialogOpen(true);

  const handleThreadDelete = async () => {
    try {
      await deleteThread({ id });
      showToast({
        message: t('threadIsDeleted'),
        tone: 'positive',
      });
      navigate(NAV_PATHS.communityHome);
    } catch (err) {
      const deleteError = err instanceof DeleteError ? err : new DeleteError();
      const message = errorTranslation(deleteError.key);
      showToast({
        message,
        tone: 'critical',
      });
    }
  };
  const handleCommentDelete = async () => {
    try {
      await deleteComment({ commentId: id });
      threadDetailDispatch({
        type: 'DELETE_COMMENT',
        payload: { commentId: id },
      });
      showToast({
        message: t('commentIsDeleted'),
        tone: 'positive',
      });
    } catch (err) {
      const deleteError = err instanceof DeleteError ? err : new DeleteError();
      const message = errorTranslation(deleteError.key);
      showToast({
        message,
        tone: 'critical',
      });
    }
  };

  const handleReplyDelete = async () => {
    try {
      deleteReply({ replyId: id });
      showToast({
        message: t('commentIsDeleted'),
        tone: 'positive',
      });
    } catch (err) {
      const deleteError = err instanceof DeleteError ? err : new DeleteError();
      const message = errorTranslation(deleteError.key);
      showToast({
        message,
        tone: 'critical',
      });
    }
  };

  const handleDeleteSubmit = async () => {
    switch (interaction) {
      case 'thread':
        await handleThreadDelete();
        break;
      case 'comment':
        await handleCommentDelete();
        break;
      case 'reply':
        await handleReplyDelete();
        break;
      default:
        break;
    }
    setIsDialogOpen(false);
  };

  return (
    <>
      {isDialogOpen && (
        <Box className={styles.mobileContainer}>
          <Dialog
            id="delete-dialog"
            title={
              interaction === 'thread'
                ? t('deleteThreadTitle')
                : t('deleteCommentTitle')
            }
            description={
              <Text tone="secondary">
                {interaction === 'thread'
                  ? t('deleteThreadDescription')
                  : t('deleteCommentDescription')}
              </Text>
            }
            open={isDialogOpen}
            onClose={handleDialogClose}
            width="small"
            closeLabel={t('cancelButton')}
          >
            <Inline space="small" align="left">
              <Button
                variant="ghost"
                tone="formAccent"
                onClick={handleDeleteSubmit}
              >
                {t('deleteButton')}
              </Button>
              <Button
                variant="transparent"
                tone="formAccent"
                onClick={handleDialogClose}
              >
                {t('cancelButton')}
              </Button>
            </Inline>
          </Dialog>
        </Box>
      )}
      {!isDialogOpen && (
        <Box
          className={styles.mobileContainer}
          id="options-dropdown-menu-button"
        >
          <OverflowMenu label="Options">
            <MenuItem onClick={handleDialogOpen}>{t('deleteButton')}</MenuItem>
            <MenuItemLink href={COMMUNITY_GUIDLINES} target="_blank">
              {t('guidelinesButoon')}
            </MenuItemLink>
          </OverflowMenu>
        </Box>
      )}
    </>
  );
};
